// @ts-nocheck - untyped
import React, { useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseIcon from '@mui/icons-material/Close';

import { buildChartData, ChartCard, getSeriesStats } from './MetricsCharts';

import './ServicesMonitor.scss';

const COMPACT_CHART_HEIGHT = 160;

const styles = {
  gridItemWrapper: { height: '100%', position: 'relative' },
  chartPaperGrid: 'sm-grid-chart-paper',
  chartPaper: 'sm-chart-paper',
  chartTitle: 'sm-chart-title',
  chartTitleCompact: 'sm-grid-chart-title-compact',
  fullscreenBtn: {
    position: 'absolute',
    top: 6,
    right: 6,
    zIndex: 1,
    color: 'text.secondary',
    opacity: 0.5,
    '&:hover': { opacity: 1 },
  },
};

export default function MetricsGridLayout({ series = [], metadata = {}, title, cols = 4 }) {
  const { data, seriesConfig } = useMemo(() => buildChartData(series), [series]);
  const hasData = data.length > 0 && seriesConfig.some(c => data.some(d => d[c.key] != null));
  const [expandedChartKey, setExpandedChartKey] = useState(null);

  const metricsSummary = seriesConfig.length > 0 ? seriesConfig.map(c => c.name).join(' · ') : '';
  const pointCount = data.length;
  const extraSparklines = seriesConfig.length === 2 ? seriesConfig : [];
  const gridItems = [
    ...seriesConfig.map(c => ({ type: 'chart', config: c, idx: seriesConfig.indexOf(c) })),
    ...extraSparklines.map(c => ({ type: 'sparkline', config: c, idx: seriesConfig.indexOf(c) })),
  ];

  if (!hasData) {
    return (
      <Paper className="sm-grid-chart-paper" elevation={0}>
        <Typography className="sm-grid-chart-title" variant="subtitle1">
          {title || 'Metrics'}
        </Typography>
        <Box className="sm-chart-empty">
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            No data for the selected period.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
            Try another time range or generate some traffic.
          </Typography>
          {metadata?.dataPointCount === 0 && (
            <Typography variant="body2" sx={{ mt: 1.5, fontStyle: 'italic' }}>
              Data points: 0. Metrics may take 1-2 minutes to appear in CloudWatch.
            </Typography>
          )}
        </Box>
      </Paper>
    );
  }

  return (
    <Box>
      {metricsSummary && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 1.5, px: 0.5, fontWeight: 500 }}
          title="Metrics and data points in the charts below"
        >
          Shown: {metricsSummary}
          {pointCount > 0 && ` · ${pointCount.toLocaleString()} data points`}
        </Typography>
      )}
      <Box
        className="sm-grid-chart-grid"
        sx={{ gridTemplateColumns: `repeat(${Math.max(1, Math.min(cols, 6))}, 1fr)` }}
      >
        {gridItems.map(item => (
          <Box
            key={item.type === 'sparkline' ? `spark-${item.config.key}` : item.config.key}
            sx={styles.gridItemWrapper}
          >
            <Paper className={styles.chartPaperGrid} elevation={0}>
              <Typography
                className={
                  cols >= 4 ? `${styles.chartTitle} ${styles.chartTitleCompact}` : styles.chartTitle
                }
                variant="subtitle1"
              >
                {title ? `${title} - ${item.config.name}` : item.config.name}
                {item.config.unit && (
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ ml: 0.5, color: 'text.secondary', fontSize: 11 }}
                  >
                    ({item.config.unit})
                  </Typography>
                )}
              </Typography>
              <IconButton
                sx={styles.fullscreenBtn}
                size="small"
                onClick={() => setExpandedChartKey(item.config.key)}
                aria-label="Expand chart"
              >
                <FullscreenIcon fontSize="small" />
              </IconButton>
              <ChartCard
                config={item.config}
                data={data}
                title=""
                useGridStyle
                styles={styles}
                colorIndex={item.idx}
                hideTitle
                noPaper
                chartHeight={COMPACT_CHART_HEIGHT}
              />
            </Paper>
          </Box>
        ))}
      </Box>

      <Dialog
        fullScreen
        open={expandedChartKey != null}
        onClose={() => setExpandedChartKey(null)}
        slotProps={{
          backdrop: {
            sx: { backdropFilter: 'blur(8px)', backgroundColor: 'rgba(0,0,0,0.35)' },
          },
        }}
        PaperProps={{
          component: 'div',
          onClick: () => setExpandedChartKey(null),
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
            cursor: 'default',
          },
        }}
      >
        {expandedChartKey != null &&
          (() => {
            const config = seriesConfig.find(c => c.key === expandedChartKey);
            const idx = seriesConfig.findIndex(c => c.key === expandedChartKey);
            if (!config) return null;
            const stats = getSeriesStats(data, config.key);
            const formatVal = (v, u) => {
              if (v == null || Number.isNaN(Number(v))) return '-';
              const n = Number(v);
              const s = Number.isInteger(n) ? n.toLocaleString() : n.toFixed(2);
              return u ? `${s} ${u}` : s;
            };
            const peakRow =
              data.length && stats.max != null
                ? data.find(r => Number(r[config.key]) === stats.max)
                : null;
            const lowRow =
              data.length && stats.min != null
                ? data.find(r => Number(r[config.key]) === stats.min)
                : null;
            const firstVal = data.length ? data[0][config.key] : null;
            const lastVal = data.length ? data[data.length - 1][config.key] : null;
            const trend =
              firstVal != null && lastVal != null
                ? Number(lastVal) > Number(firstVal)
                  ? 'Rising'
                  : Number(lastVal) < Number(firstVal)
                    ? 'Falling'
                    : 'Stable'
                : null;
            return (
              <DialogContent
                onClick={e => e.stopPropagation()}
                sx={{
                  p: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundColor: 'var(--t-surface)',
                  borderRadius: 1,
                  boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
                  border: '1px solid rgba(94, 51, 191, 0.2)',
                  maxWidth: '94vw',
                  maxHeight: '90vh',
                  width: 920,
                  overflow: 'auto',
                  cursor: 'default',
                  position: 'relative',
                }}
              >
                <Box sx={{ position: 'absolute', top: 28, right: 16, zIndex: 2 }}>
                  <IconButton
                    onClick={() => setExpandedChartKey(null)}
                    aria-label="Close"
                    sx={{
                      color: 'text.primary',
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
                <Box sx={{ flex: 1, p: 3, pt: 4, minHeight: 480 }}>
                  <Typography className={styles.chartTitle} sx={{ mb: 1 }}>
                    {title ? `${title} - ${config.name}` : config.name}
                    {config.unit && (
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{ ml: 1, color: 'text.secondary' }}
                      >
                        ({config.unit})
                      </Typography>
                    )}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 2,
                      mb: 2,
                      fontSize: 13,
                      color: 'text.secondary',
                      '& strong': { color: 'text.primary' },
                    }}
                  >
                    <span>
                      <strong>Min:</strong> {formatVal(stats.min, config.unit)}
                    </span>
                    <span>
                      <strong>Max:</strong> {formatVal(stats.max, config.unit)}
                    </span>
                    <span>
                      <strong>Avg:</strong> {formatVal(stats.avg, config.unit)}
                    </span>
                    <span>
                      <strong>Data points:</strong> {stats.count}
                    </span>
                    {stats.count > 0 && (
                      <span>
                        <strong>Total:</strong>{' '}
                        {formatVal(
                          data.reduce((a, r) => a + (Number(r[config.key]) || 0), 0),
                          config.unit
                        )}
                      </span>
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                      gap: 2,
                      mb: 2,
                      p: 2,
                      borderRadius: 2,
                      bgcolor: 'grey.50',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {peakRow && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Peak (max value)
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {formatVal(stats.max, config.unit)} at {peakRow.time}
                        </Typography>
                      </Box>
                    )}
                    {lowRow && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Low (min value)
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {formatVal(stats.min, config.unit)} at {lowRow.time}
                        </Typography>
                      </Box>
                    )}
                    {data.length >= 2 && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Period
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {data[0].time} → {data[data.length - 1].time}
                        </Typography>
                      </Box>
                    )}
                    {trend && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Trend
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {trend}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <ChartCard
                    config={config}
                    data={data}
                    title=""
                    useGridStyle={false}
                    styles={styles}
                    colorIndex={idx >= 0 ? idx : 0}
                    hideTitle
                    noPaper
                    chartHeight={380}
                  />
                  {(() => {
                    const key = expandedChartKey;
                    const rowsWithValue = (data || []).filter(
                      r => r[config.key] != null && !Number.isNaN(Number(r[config.key]))
                    );
                    if (key === 'errors') {
                      const errorEvents = rowsWithValue.filter(r => Number(r[config.key]) > 0);
                      return (
                        <Box sx={{ mt: 2 }}>
                          {errorEvents.length > 0 ? (
                            <>
                              <Typography
                                variant="subtitle2"
                                sx={{ mb: 1, color: 'text.secondary', fontWeight: 600 }}
                              >
                                When errors occurred ({errorEvents.length} interval
                                {errorEvents.length !== 1 ? 's' : ''})
                              </Typography>
                              <Table size="small" sx={{ '& td, & th': { py: 0.5, fontSize: 12 } }}>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Time</TableCell>
                                    <TableCell align="right">Error count</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {errorEvents.map((row, i) => (
                                    <TableRow key={i}>
                                      <TableCell>{row.time}</TableCell>
                                      <TableCell align="right">
                                        {formatVal(row[config.key], '')}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mt: 1, display: 'block' }}
                              >
                                For status codes (4xx/5xx) and error details, check application logs
                                for the period above.
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No errors recorded in this period. For individual status codes use
                              application logs.
                            </Typography>
                          )}
                        </Box>
                      );
                    }
                    if (key === 'rate') {
                      const sorted = [...rowsWithValue].sort(
                        (a, b) => Number(b[config.key]) - Number(a[config.key])
                      );
                      const top5 = sorted.slice(0, 5).filter(r => Number(r[config.key]) > 0);
                      return top5.length > 0 ? (
                        <Box sx={{ mt: 2 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ mb: 1, color: 'text.secondary', fontWeight: 600 }}
                          >
                            Peak traffic (top 5 intervals)
                          </Typography>
                          <Table size="small" sx={{ '& td, & th': { py: 0.5, fontSize: 12 } }}>
                            <TableHead>
                              <TableRow>
                                <TableCell>Time</TableCell>
                                <TableCell align="right">Requests</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {top5.map((row, i) => (
                                <TableRow key={i}>
                                  <TableCell>{row.time}</TableCell>
                                  <TableCell align="right">
                                    {formatVal(row[config.key], config.unit)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      ) : null;
                    }
                    if (key === 'latency') {
                      const sorted = [...rowsWithValue].sort(
                        (a, b) => Number(b[config.key]) - Number(a[config.key])
                      );
                      const top5 = sorted.slice(0, 5).filter(r => Number(r[config.key]) > 0);
                      return top5.length > 0 ? (
                        <Box sx={{ mt: 2 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ mb: 1, color: 'text.secondary', fontWeight: 600 }}
                          >
                            Slowest intervals (top 5)
                          </Typography>
                          <Table size="small" sx={{ '& td, & th': { py: 0.5, fontSize: 12 } }}>
                            <TableHead>
                              <TableRow>
                                <TableCell>Time</TableCell>
                                <TableCell align="right">Latency (ms)</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {top5.map((row, i) => (
                                <TableRow key={i}>
                                  <TableCell>{row.time}</TableCell>
                                  <TableCell align="right">
                                    {formatVal(row[config.key], config.unit)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      ) : null;
                    }
                    if (key === 'saturation') {
                      const saturationEvents = rowsWithValue.filter(r => Number(r[config.key]) > 0);
                      return saturationEvents.length > 0 ? (
                        <Box sx={{ mt: 2 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ mb: 1, color: 'text.secondary', fontWeight: 600 }}
                          >
                            When saturation &gt; 0 ({saturationEvents.length} interval
                            {saturationEvents.length !== 1 ? 's' : ''})
                          </Typography>
                          <Table size="small" sx={{ '& td, & th': { py: 0.5, fontSize: 12 } }}>
                            <TableHead>
                              <TableRow>
                                <TableCell>Time</TableCell>
                                <TableCell align="right">Saturation (%)</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {saturationEvents.map((row, i) => (
                                <TableRow key={i}>
                                  <TableCell>{row.time}</TableCell>
                                  <TableCell align="right">
                                    {formatVal(row[config.key], '')}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      ) : null;
                    }
                    return null;
                  })()}
                </Box>
              </DialogContent>
            );
          })()}
      </Dialog>
    </Box>
  );
}
